import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader, X as XIcon, Plus, X, ChevronDown, Info } from 'lucide-react';
import { QuestionBankCreate, Question, useGetQuestionBankFilterValuesQuery } from '../../redux/api/qaApi';

const FieldTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative group/tip ml-1 inline-flex">
    <Info className="w-3.5 h-3.5 text-[var(--color-text-muted)] cursor-help" />
    <span className="absolute left-0 top-full mt-2 w-64 px-3 py-2 rounded-lg bg-[var(--color-surface-raised)] border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)] leading-relaxed shadow-lg opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity z-[100002]">
      {text}
    </span>
  </span>
);

interface CreateQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: QuestionBankCreate) => void;
  isLoading?: boolean;
  question?: Question | null; // Optional question for edit mode
  tenantId?: string | null;
}

const questionBankCreateSchema = z.object({
  check_description: z.string().min(1, 'Check description is required'),
  qc_type: z.string().min(1, 'QC type is required').max(100, 'QC type must be 100 characters or less'),
  category: z.string().min(1, 'Category is required').max(100, 'Category must be 100 characters or less'),
  first_appears_at: z.string().default(''),
  applicable_sheet_types: z.array(z.string()).default([]),
  validators: z.array(z.string()).default([]),
  cross_reference_sheets: z.array(z.string()).default([]),
  validation_trigger: z.string().min(1, 'Question scope is required').max(50, 'Question scope must be 50 characters or less'),
  meta: z.record(z.any()).default({}),
});

type QuestionBankCreateFormData = z.infer<typeof questionBankCreateSchema>;

const CreateQuestionModal: React.FC<CreateQuestionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  question = null,
  tenantId = null,
}) => {
  const isEditMode = !!question;
  const { data: filterValues, isLoading: isLoadingFilterValues, error: filterValuesError } = useGetQuestionBankFilterValuesQuery(
    tenantId ? { tenant_id: tenantId } : undefined,
    { skip: !isOpen },
  );

  // Debug: Log filter values to verify data is loading (only when modal opens)
  useEffect(() => {
    if (isOpen && filterValues) {
      console.log('Modal opened, filter values:', filterValues);
      console.log('Loading:', isLoadingFilterValues);
      console.log('Error:', filterValuesError);
    }
  }, [isOpen, filterValues, isLoadingFilterValues, filterValuesError]);

  const [newSheetType, setNewSheetType] = useState('');
  const [newCrossRef, setNewCrossRef] = useState('');

  // Single-select dropdown state
  const [qcTypeSearch, setQcTypeSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [scopeSearch, setScopeSearch] = useState('');
  const [qcTypeDropdownOpen, setQcTypeDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const qcTypeDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const scopeDropdownRef = useRef<HTMLDivElement>(null);

  const [sheetTypeDropdownOpen, setSheetTypeDropdownOpen] = useState(false);
  const [crossRefDropdownOpen, setCrossRefDropdownOpen] = useState(false);
  const sheetTypeDropdownRef = useRef<HTMLDivElement>(null);
  const crossRefDropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<QuestionBankCreateFormData>({
    resolver: zodResolver(questionBankCreateSchema),
    defaultValues: {
      check_description: '',
      qc_type: '',
      category: '',
      first_appears_at: '',
      applicable_sheet_types: [],
      validators: [],
      cross_reference_sheets: [],
      validation_trigger: '',
      meta: {},
    },
  });

  const applicableSheetTypes = watch('applicable_sheet_types');
  const crossReferenceSheets = watch('cross_reference_sheets');
  const qcType = watch('qc_type');
  const category = watch('category');
  const validationTrigger = watch('validation_trigger');

  // Helper function to filter by substring, ignoring underscores
  const filterBySubstring = (items: string[], searchTerm: string): string[] => {
    if (!searchTerm.trim()) return items;
    const normalizedSearch = searchTerm.toLowerCase().replace(/_/g, '');
    return items.filter(item => {
      const normalizedItem = item.toLowerCase().replace(/_/g, '');
      return normalizedItem.includes(normalizedSearch);
    });
  };

  // Format question scope values with alias: "immediate" → "Per Page"
  const formatQuestionScope = (text: string): string => {
    if (!text) return '';
    if (text.toLowerCase() === 'immediate') return 'Per Page';
    return formatDisplayText(text);
  };

  // Helper function to format display text (remove underscores, capitalize words)
  const formatDisplayText = (text: string): string => {
    if (!text) return '';
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to convert display text back to underscored format
  // Used when user types manually - converts spaces to underscores and lowercases
  const convertToUnderscoreFormat = (text: string): string => {
    if (!text) return '';
    return text
      .trim()
      .split(/\s+/)
      .map(word => word.toLowerCase())
      .join('_');
  };

  // Helper to find matching underscored value from a list
  const findMatchingUnderscoredValue = (displayText: string, options: string[]): string | null => {
    const normalized = displayText.toLowerCase().replace(/\s+/g, '_');
    return options.find(opt => opt.toLowerCase() === normalized) || null;
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!qcTypeDropdownRef.current?.contains(target)) setQcTypeDropdownOpen(false);
      if (!categoryDropdownRef.current?.contains(target)) setCategoryDropdownOpen(false);
      if (!scopeDropdownRef.current?.contains(target)) setScopeDropdownOpen(false);
      if (!sheetTypeDropdownRef.current?.contains(target)) setSheetTypeDropdownOpen(false);
      if (!crossRefDropdownRef.current?.contains(target)) setCrossRefDropdownOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Pre-populate form if editing
      if (question) {
        reset({
          check_description: question.check_description || '',
          qc_type: question.qc_type || '',
          category: question.category || '',
          first_appears_at: question.first_appears_at || '',
          applicable_sheet_types: question.applicable_sheet_types || [],
          validators: question.validators || [],
          cross_reference_sheets: question.cross_reference_sheets || [],
          validation_trigger: question.validation_trigger || '',
          meta: question.meta || {},
        });
      }
    } else {
      document.body.style.overflow = '';
      // Reset form when closing
      reset({
        check_description: '',
        qc_type: '',
        category: '',
        first_appears_at: '',
        applicable_sheet_types: [],
        validators: [],
        cross_reference_sheets: [],
        validation_trigger: '',
        meta: {},
      });
      setNewSheetType('');
      setNewCrossRef('');
      setQcTypeSearch('');
      setCategorySearch('');
      setScopeSearch('');
      setQcTypeDropdownOpen(false);
      setCategoryDropdownOpen(false);
      setScopeDropdownOpen(false);
      setSheetTypeDropdownOpen(false);
      setCrossRefDropdownOpen(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, reset, question]);

  const onFormSubmit = (data: QuestionBankCreateFormData) => {
    onSubmit({
      ...data,
      first_appears_at: data.applicable_sheet_types?.[0] || '',
      validators: ['qa_response_structure'],
      meta: {},
    });
  };

  const handleClose = () => {
    onClose();
  };

  const addSheetType = () => {
    if (newSheetType.trim()) {
      // Try to find matching underscored value, otherwise convert to underscore format
      const matched = findMatchingUnderscoredValue(newSheetType, uniqueApplicableSheetTypes);
      const valueToAdd = matched || convertToUnderscoreFormat(newSheetType);
      if (!applicableSheetTypes?.includes(valueToAdd)) {
        setValue('applicable_sheet_types', [...(applicableSheetTypes || []), valueToAdd], {
          shouldValidate: true,
        });
        setNewSheetType('');
      }
    }
  };

  const removeSheetType = (index: number) => {
    const updated = applicableSheetTypes?.filter((_, i) => i !== index) || [];
    setValue('applicable_sheet_types', updated, { shouldValidate: true });
  };

  const addCrossRef = () => {
    if (newCrossRef.trim()) {
      const matched = findMatchingUnderscoredValue(newCrossRef, uniqueCrossReferenceSheets);
      const valueToAdd = matched || convertToUnderscoreFormat(newCrossRef);
      if (!crossReferenceSheets?.includes(valueToAdd)) {
        setValue('cross_reference_sheets', [...(crossReferenceSheets || []), valueToAdd], {
          shouldValidate: true,
        });
        setNewCrossRef('');
      }
    }
  };

  const removeCrossRef = (index: number) => {
    const updated = crossReferenceSheets?.filter((_, i) => i !== index) || [];
    setValue('cross_reference_sheets', updated, { shouldValidate: true });
  };

  // Safely extract values, handling both array and undefined/null cases
  const uniqueCategories = Array.isArray(filterValues?.categories) ? filterValues.categories : [];
  const uniqueQcTypes = Array.isArray(filterValues?.qc_types) ? filterValues.qc_types : [];
  const uniqueFirstAppearsAt = Array.isArray(filterValues?.first_appears_at) ? filterValues.first_appears_at : [];
  const uniqueValidationTriggers = Array.isArray(filterValues?.validation_triggers) ? filterValues.validation_triggers : [];
  const uniqueApplicableSheetTypes = Array.isArray(filterValues?.applicable_sheet_types) ? filterValues.applicable_sheet_types : [];
  const uniqueValidators = Array.isArray(filterValues?.validators) ? filterValues.validators : [];
  const uniqueCrossReferenceSheets = Array.isArray(filterValues?.cross_reference_sheets) ? filterValues.cross_reference_sheets : [];

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[var(--color-modal-backdrop)]"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-[var(--color-modal-bg)] border border-[var(--color-border)] rounded-lg w-full max-w-3xl mx-4 relative max-h-[90vh] flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: 100000 }}
      >
        <div className="p-6 pb-4 flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            disabled={isLoading}
          >
            <XIcon className="w-5 h-5" />
          </button>
          <h3 className="text-xl font-bold text-[var(--color-text-primary)] mb-4">
            {isEditMode ? 'Edit Question' : 'Add New Question'}
          </h3>
        </div>
        <form onSubmit={handleSubmit(onFormSubmit)} className="px-6 pb-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Question */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              Question *
              <FieldTooltip text="The question or check the agent will evaluate on each applicable page." />
            </label>
            <textarea
              {...register('check_description')}
              className={`w-full px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border ${
                errors.check_description
                  ? 'border-red-500'
                  : 'border-[var(--color-input-border)]'
              } text-[var(--color-text-primary)] focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none`}
              placeholder="Please describe the check in detail here"
              rows={3}
              disabled={isLoading}
            />
            {errors.check_description && (
              <p className="text-xs text-red-400 mt-1">{errors.check_description.message}</p>
            )}
          </div>

          {/* QC Type */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              QC Type *
              <FieldTooltip text="Classification of the check: Drawing QC (visual/formatting) vs Design QC (engineering correctness)." />
            </label>
            <div className="relative" ref={qcTypeDropdownRef}>
              <button
                type="button"
                onClick={() => { setQcTypeDropdownOpen(!qcTypeDropdownOpen); setQcTypeSearch(''); }}
                className={`w-full px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border ${
                  errors.qc_type ? 'border-red-500' : 'border-[var(--color-input-border)]'
                } text-left flex items-center justify-between focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none`}
                disabled={isLoading}
              >
                <span className={qcType ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-input-placeholder)]'}>
                  {qcType ? formatDisplayText(qcType) : 'Select QC type'}
                </span>
                <ChevronDown className={`w-4 h-4 text-[var(--color-text-secondary)] transition-transform ${qcTypeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {qcTypeDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg z-[100001] border border-[var(--color-border)] overflow-hidden">
                  <div className="p-2 border-b border-[var(--color-border)]">
                    <input
                      type="text"
                      value={qcTypeSearch}
                      onChange={(e) => setQcTypeSearch(e.target.value)}
                      className="w-full px-2 py-1.5 rounded bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-input-placeholder)] focus:border-[#17c480] focus:outline-none"
                      placeholder="Filter..."
                      autoFocus
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {(() => {
                      const filtered = filterBySubstring(uniqueQcTypes, qcTypeSearch);
                      return filtered.length > 0 ? filtered.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => { setValue('qc_type', opt, { shouldValidate: true }); setQcTypeDropdownOpen(false); setQcTypeSearch(''); }}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors text-[var(--color-text-primary)] ${opt === qcType ? 'bg-engramiq-green/15 text-engramiq-green' : 'hover:bg-[var(--color-dropdown-item-hover)]'}`}
                        >
                          {formatDisplayText(opt)}
                        </button>
                      )) : (
                        <div className="p-3 text-sm text-[var(--color-text-secondary)] text-center">No matches found</div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {errors.qc_type && (
              <p className="text-xs text-red-400 mt-1">{errors.qc_type.message}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              Category *
              <FieldTooltip text="Grouping label for organizing related checks (e.g. Annotations, Titleblock)." />
            </label>
            <div className="relative" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => { setCategoryDropdownOpen(!categoryDropdownOpen); setCategorySearch(''); }}
                className={`w-full px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border ${
                  errors.category ? 'border-red-500' : 'border-[var(--color-input-border)]'
                } text-left flex items-center justify-between focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none`}
                disabled={isLoading}
              >
                <span className={category ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-input-placeholder)]'}>
                  {category ? formatDisplayText(category) : 'Select category'}
                </span>
                <ChevronDown className={`w-4 h-4 text-[var(--color-text-secondary)] transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg z-[100001] border border-[var(--color-border)] overflow-hidden">
                  <div className="p-2 border-b border-[var(--color-border)]">
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="w-full px-2 py-1.5 rounded bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-input-placeholder)] focus:border-[#17c480] focus:outline-none"
                      placeholder="Filter..."
                      autoFocus
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {(() => {
                      const filtered = filterBySubstring(uniqueCategories, categorySearch);
                      return filtered.length > 0 ? filtered.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => { setValue('category', opt, { shouldValidate: true }); setCategoryDropdownOpen(false); setCategorySearch(''); }}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors text-[var(--color-text-primary)] ${opt === category ? 'bg-engramiq-green/15 text-engramiq-green' : 'hover:bg-[var(--color-dropdown-item-hover)]'}`}
                        >
                          {formatDisplayText(opt)}
                        </button>
                      )) : (
                        <div className="p-3 text-sm text-[var(--color-text-secondary)] text-center">No matches found</div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {errors.category && (
              <p className="text-xs text-red-400 mt-1">{errors.category.message}</p>
            )}
          </div>

          {/* Question Scope */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              Question Scope *
              <FieldTooltip text="Per Page = can be answered from a single page. Cross Sheet = requires comparing multiple sheet types." />
            </label>
            <div className="relative" ref={scopeDropdownRef}>
              <button
                type="button"
                onClick={() => { setScopeDropdownOpen(!scopeDropdownOpen); setScopeSearch(''); }}
                className={`w-full px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border ${
                  errors.validation_trigger ? 'border-red-500' : 'border-[var(--color-input-border)]'
                } text-left flex items-center justify-between focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none`}
                disabled={isLoading}
              >
                <span className={validationTrigger ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-input-placeholder)]'}>
                  {validationTrigger ? formatQuestionScope(validationTrigger) : 'Select question scope'}
                </span>
                <ChevronDown className={`w-4 h-4 text-[var(--color-text-secondary)] transition-transform ${scopeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {scopeDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg z-[100001] border border-[var(--color-border)] overflow-hidden">
                  <div className="p-2 border-b border-[var(--color-border)]">
                    <input
                      type="text"
                      value={scopeSearch}
                      onChange={(e) => setScopeSearch(e.target.value)}
                      className="w-full px-2 py-1.5 rounded bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-input-placeholder)] focus:border-[#17c480] focus:outline-none"
                      placeholder="Filter..."
                      autoFocus
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {(() => {
                      const filtered = uniqueValidationTriggers.filter((item) => {
                        if (!scopeSearch.trim()) return true;
                        const search = scopeSearch.toLowerCase();
                        return item.toLowerCase().includes(search) || formatQuestionScope(item).toLowerCase().includes(search);
                      });
                      return filtered.length > 0 ? filtered.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => { setValue('validation_trigger', opt, { shouldValidate: true }); setScopeDropdownOpen(false); setScopeSearch(''); }}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors text-[var(--color-text-primary)] ${opt === validationTrigger ? 'bg-engramiq-green/15 text-engramiq-green' : 'hover:bg-[var(--color-dropdown-item-hover)]'}`}
                        >
                          {formatQuestionScope(opt)}
                        </button>
                      )) : (
                        <div className="p-3 text-sm text-[var(--color-text-secondary)] text-center">No matches found</div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            {errors.validation_trigger && (
              <p className="text-xs text-red-400 mt-1">{errors.validation_trigger.message}</p>
            )}
          </div>

          {/* Applicable Sheet Types */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              Applicable Sheet Types
              <FieldTooltip text="Sheet types this question will be asked on. Each page matching these types gets assigned this question." />
            </label>
            <div className="relative flex gap-2 mb-2" ref={sheetTypeDropdownRef}>
              <input
                type="text"
                value={newSheetType}
                onChange={(e) => setNewSheetType(e.target.value)}
                onFocus={() => setSheetTypeDropdownOpen(true)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSheetType();
                  }
                }}
                className="flex-1 px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text-primary)] focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                placeholder="Select or enter sheet type"
                disabled={isLoading}
              />
              {sheetTypeDropdownOpen && (
                <div 
                  className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg z-[100001] max-h-48 overflow-y-auto border border-[var(--color-border)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const available = uniqueApplicableSheetTypes.filter((type) => !applicableSheetTypes?.includes(type));
                    const filtered = filterBySubstring(available, newSheetType);
                    return filtered.length > 0 ? (
                      filtered.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (type.trim() && !applicableSheetTypes?.includes(type.trim())) {
                              setValue('applicable_sheet_types', [...(applicableSheetTypes || []), type.trim()], {
                                shouldValidate: true,
                              });
                              setNewSheetType('');
                              setSheetTypeDropdownOpen(false);
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-dropdown-item-hover)] transition-colors text-[var(--color-text-primary)]"
                        >
                          {formatDisplayText(type)}
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-[var(--color-text-secondary)] text-center">
                        {available.length === 0 ? 'All values already added' : 'No matches found'}
                      </div>
                    );
                  })()}
                </div>
              )}
              <button
                type="button"
                onClick={addSheetType}
                disabled={isLoading}
                className="px-3 py-2 bg-engramiq-green/20 hover:bg-engramiq-green/30 rounded-lg text-engramiq-green transition-all disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {applicableSheetTypes && applicableSheetTypes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {applicableSheetTypes.map((type, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-purple-400/20 text-purple-400 rounded text-xs flex items-center gap-1"
                  >
                    {formatDisplayText(type)}
                    <button
                      type="button"
                      onClick={() => removeSheetType(index)}
                      className="hover:text-purple-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Cross Reference Sheets */}
          <div>
            <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 flex items-center">
              Cross Reference Sheets
              <FieldTooltip text="Additional sheet types to include as context pages in the task, without assigning this question to those pages. Use when the agent needs to see other sheets to cross-check its answer." />
            </label>
            <div className="relative flex gap-2 mb-2" ref={crossRefDropdownRef}>
              <input
                type="text"
                value={newCrossRef}
                onChange={(e) => setNewCrossRef(e.target.value)}
                onFocus={() => setCrossRefDropdownOpen(true)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCrossRef();
                  }
                }}
                className="flex-1 px-3 py-2 rounded-lg bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text-primary)] focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                placeholder="Select or enter cross reference sheet"
                disabled={isLoading}
              />
              {crossRefDropdownOpen && (
                <div 
                  className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-dropdown-bg)] rounded-lg shadow-lg z-[100001] max-h-48 overflow-y-auto border border-[var(--color-border)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const available = uniqueCrossReferenceSheets.filter((ref) => !crossReferenceSheets?.includes(ref));
                    const filtered = filterBySubstring(available, newCrossRef);
                    return filtered.length > 0 ? (
                      filtered.map((ref) => (
                        <button
                          key={ref}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (ref.trim() && !crossReferenceSheets?.includes(ref.trim())) {
                              setValue('cross_reference_sheets', [...(crossReferenceSheets || []), ref.trim()], {
                                shouldValidate: true,
                              });
                              setNewCrossRef('');
                              setCrossRefDropdownOpen(false);
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-dropdown-item-hover)] transition-colors text-[var(--color-text-primary)]"
                        >
                          {formatDisplayText(ref)}
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-[var(--color-text-secondary)] text-center">
                        {available.length === 0 ? 'All values already added' : 'No matches found'}
                      </div>
                    );
                  })()}
                </div>
              )}
              <button
                type="button"
                onClick={addCrossRef}
                disabled={isLoading}
                className="px-3 py-2 bg-engramiq-green/20 hover:bg-engramiq-green/30 rounded-lg text-engramiq-green transition-all disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {crossReferenceSheets && crossReferenceSheets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {crossReferenceSheets.map((ref, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-orange-400/20 text-orange-400 rounded text-xs flex items-center gap-1"
                  >
                    {formatDisplayText(ref)}
                    <button
                      type="button"
                      onClick={() => removeCrossRef(index)}
                      className="hover:text-orange-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="eg-btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="eg-btn-primary flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Question' : 'Create Question'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  if (!portalTarget) return null;

  return createPortal(modalContent, portalTarget);
};

export default CreateQuestionModal;
